import * as z from "zod";
import { PRODUCT_IDS, type ProductId } from "./schema/initialize";

interface OnboardingBookingDestinationConfig {
  defaultUrl: string;
  label: string;
}

export const DEFAULT_RETURNS_ONBOARDING_HELP_URL =
  "https://calendly.com/hugh-getredo/new-customer-returns-onboarding";

const ONBOARDING_BOOKING_PRODUCT_CONFIG = {
  returns: {
    defaultUrl:
      "https://meetings.hubspot.com/brandon-walker/self-serve-booking?uuid=f3bcac86-c6ff-40df-8c5e-471b92e56ae2",
    label: "Returns",
  },
  "order-editing": {
    defaultUrl:
      "https://calendar.google.com/calendar/appointments/schedules/AcZssZ0waqwls1lUf-Gs6BpBE8qa3Ih0C5FJ2RS_6eEluatBFlIr9WAJGsRiY7A2OqFb-CQw4OHRiyhk?gv=true",
    label: "Order Editing",
  },
  "shipping-fulfillment": {
    defaultUrl: "https://calendly.com/suz-getredo/30-minute-meeting",
    label: "Shipping & Fulfillment",
  },
  chargebacks: {
    defaultUrl: "https://calendly.com/cameron-buford-getredo/30min",
    label: "Chargebacks",
  },
  recover: {
    defaultUrl:
      "https://calendly.com/d/cw85-d74-6r6/redo-onboarding-support?primary_color=000000",
    label: "Recover",
  },
  contextual: {
    defaultUrl:
      "https://calendly.com/d/cw85-d74-6r6/redo-onboarding-support?primary_color=000000",
    label: "Contextual",
  },
  "email-sms": {
    defaultUrl:
      "https://meetings.hubspot.com/bthurgood/emailsms-round-robin?embed=true",
    label: "Email & SMS",
  },
  aeo: {
    defaultUrl:
      "https://calendly.com/jake-bengtzen-getredo/aeo-onboarding-call-jake-with-redo",
    label: "Answer Engine Optimization",
  },
  "checkout-optimization": {
    defaultUrl: "https://calendly.com/cameron-buford-getredo/30min",
    label: "Checkout Optimization",
  },
  "ai-sales-support": {
    defaultUrl:
      "https://calendar.google.com/calendar/appointments/schedules/AcZssZ1_9_C99TyeDJSjHbpR66raLI6IadNvl9260l8EW0_CpUO3QM0YUP20rA7a8UsnNhpok1NG4Hpq?gv=true",
    label: "AI Sales + Support",
  },
  "inventory-management": {
    defaultUrl: "https://calendar.app.google/SWtX4oB5QeSjLuM47",
    label: "Inventory Management",
  },
} as const satisfies Partial<
  Record<ProductId, OnboardingBookingDestinationConfig>
>;

const ADDITIONAL_ONBOARDING_BOOKING_DESTINATION_CONFIG = {
  "returns-get-help": {
    defaultUrl: DEFAULT_RETURNS_ONBOARDING_HELP_URL,
    label: "Returns Get Help",
  },
  warranties: {
    defaultUrl: "https://calendly.com/carter-johnson-redo",
    label: "Warranties",
  },
} as const satisfies Record<string, OnboardingBookingDestinationConfig>;

const ONBOARDING_BOOKING_DESTINATION_CONFIG = {
  ...ONBOARDING_BOOKING_PRODUCT_CONFIG,
  ...ADDITIONAL_ONBOARDING_BOOKING_DESTINATION_CONFIG,
} as const;

export type OnboardingBookingProduct =
  keyof typeof ONBOARDING_BOOKING_PRODUCT_CONFIG;
export type OnboardingBookingDestinationId =
  keyof typeof ONBOARDING_BOOKING_DESTINATION_CONFIG;

export function isOnboardingBookingProduct(
  productId: string,
): productId is OnboardingBookingProduct {
  return Object.prototype.hasOwnProperty.call(
    ONBOARDING_BOOKING_PRODUCT_CONFIG,
    productId,
  );
}

export const ONBOARDING_BOOKING_PRODUCTS = PRODUCT_IDS.filter(
  isOnboardingBookingProduct,
);

export const ONBOARDING_BOOKING_DESTINATIONS: readonly OnboardingBookingDestinationId[] =
  [...ONBOARDING_BOOKING_PRODUCTS, "returns-get-help", "warranties"];

export const onboardingBookingProductSchema = z
  .enum(PRODUCT_IDS)
  .pipe(
    z.custom<OnboardingBookingProduct>(
      (value) => typeof value === "string" && isOnboardingBookingProduct(value),
    ),
  );

export function isOnboardingBookingDestination(
  destinationId: string,
): destinationId is OnboardingBookingDestinationId {
  return Object.prototype.hasOwnProperty.call(
    ONBOARDING_BOOKING_DESTINATION_CONFIG,
    destinationId,
  );
}

export const onboardingBookingDestinationIdSchema =
  z.custom<OnboardingBookingDestinationId>(
    (value) =>
      typeof value === "string" && isOnboardingBookingDestination(value),
  );

const SUPPORTED_BOOKING_ORIGINS = new Set([
  "https://calendar.app.google",
  "https://calendar.google.com",
  "https://calendly.com",
  "https://meetings.hubspot.com",
]);

export function getDefaultOnboardingBookingUrl(
  destinationId: OnboardingBookingDestinationId,
): string;
export function getDefaultOnboardingBookingUrl(
  destinationId: string,
): string | undefined;
export function getDefaultOnboardingBookingUrl(
  destinationId: string,
): string | undefined {
  return isOnboardingBookingDestination(destinationId)
    ? ONBOARDING_BOOKING_DESTINATION_CONFIG[destinationId].defaultUrl
    : undefined;
}

export function getOnboardingBookingDestinationLabel(
  destinationId: OnboardingBookingDestinationId,
): string {
  return ONBOARDING_BOOKING_DESTINATION_CONFIG[destinationId].label;
}

export function isAllowedOnboardingBookingUrl(bookingUrl: string): boolean {
  try {
    const parsedUrl = new URL(bookingUrl);
    return (
      parsedUrl.username === "" &&
      parsedUrl.password === "" &&
      parsedUrl.pathname !== "/" &&
      SUPPORTED_BOOKING_ORIGINS.has(parsedUrl.origin)
    );
  } catch {
    return false;
  }
}

export const onboardingBookingDestinationSchema = z
  .object({
    // Keep the legacy field name for existing Admin RPC clients.
    productId: onboardingBookingDestinationIdSchema,
    bookingUrl: z.string().trim().url(),
  })
  .superRefine(({ bookingUrl }, ctx) => {
    if (!isAllowedOnboardingBookingUrl(bookingUrl)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["bookingUrl"],
        message:
          "Booking URLs must use Calendly, HubSpot Meetings, or Google Calendar",
      });
    }
  });

export type OnboardingBookingDestination = z.infer<
  typeof onboardingBookingDestinationSchema
>;
